import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ApiService,
    GraphQLResponse,
    Mk2Request,
    Mk2Response
} from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { ListResponseFundingBatchSummaryDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/supplier/funding-batch-summary/list-funding-batch-summary';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';
import { Money, PayablesInput, PayablesResponse } from '../../model/models';
import { SupplierDashboardInvoiceSummaryRange } from './supplier-dashboard-invoice-summary-range';

export class SupplierDashboardInvoiceSummaryRequest extends Mk2Request {
    constructor(initial?: Partial<SupplierDashboardInvoiceSummaryRequest>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

export class SupplierDashboardInvoiceSummaryResponse extends Mk2Response {
    invoiceSummaryRanges?: SupplierDashboardInvoiceSummaryRange[];
    ok?: boolean;
    totalInvoiceAmount?: Money;
    totalNumberOfInvoices?: number;

    constructor(initial?: Partial<SupplierDashboardInvoiceSummaryResponse>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

@Injectable({ providedIn: 'root' })
export class GetInvoiceSummaryCardService extends ApiService<
    SupplierDashboardInvoiceSummaryRequest,
    SupplierDashboardInvoiceSummaryResponse
> {
    public requestType = SupplierDashboardInvoiceSummaryRequest;
    public responseType = SupplierDashboardInvoiceSummaryResponse;
    public endpointName = 'supplier/SupplierDashboardInvoiceSummary/GetInvoiceSummaryCard';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName =
                        'supplier/SupplierDashboardInvoiceSummary/GetInvoiceSummaryCard';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: SupplierDashboardInvoiceSummaryRequest
    ): { query: string } | SupplierDashboardInvoiceSummaryRequest {
        if (this._isPayLaterEnabled) {
            const input: PayablesInput = {
                companyFilter: {
                    supplierIds: []
                },
                dueDateRange: {
                    startDate: new Date().toISOString().split('T')[0],
                    endDate: null
                }
            };
            const query = jsonToGraphQLQuery({
                payables: {
                    __args: { input },
                    totals: {
                        supplierOpenEligibleAmount: {
                            currencyCode: true,
                            cents: true
                        },
                        supplierOpenEligiblePayableCount: true,
                        supplierOpenEligibleAmountDayOneToThirty: {
                            currencyCode: true,
                            cents: true
                        },
                        supplierOpenEligibleAmountDayThirtyOneToSixty: {
                            currencyCode: true,
                            cents: true
                        },
                        supplierOpenEligibleAmountDaySixtyOnePlus: {
                            currencyCode: true,
                            cents: true
                        }
                    }
                }
            });
            return { query: `{${query}}` };
        }
        return req;
    }

    public responseMapper(
        res: GraphQLResponse<PayablesResponse> | SupplierDashboardInvoiceSummaryResponse
    ): SupplierDashboardInvoiceSummaryResponse {
        if (this._isPayLaterEnabled) {
            const { totals } = (res as GraphQLResponse<PayablesResponse>).data.payables;

            let invoiceSummaryRanges: SupplierDashboardInvoiceSummaryRange[] = [];

            invoiceSummaryRanges.push({
                index: 0,
                range: 'ZEROTOTHIRTY',
                totalAmount: totals.supplierOpenEligibleAmountDayOneToThirty,
                amountPercentOfTotal: isFinite(
                    totals.supplierOpenEligibleAmountDayOneToThirty?.cents! /
                        totals.supplierOpenEligibleAmount?.cents!
                )
                    ? totals.supplierOpenEligibleAmountDayOneToThirty?.cents! /
                      totals.supplierOpenEligibleAmount?.cents!
                    : 0
            });

            invoiceSummaryRanges.push({
                index: 1,
                range: 'THIRTYONETOSIXTY',
                totalAmount: totals.supplierOpenEligibleAmountDayThirtyOneToSixty,
                amountPercentOfTotal: isFinite(
                    totals.supplierOpenEligibleAmountDayThirtyOneToSixty?.cents! /
                        totals.supplierOpenEligibleAmount?.cents!
                )
                    ? totals.supplierOpenEligibleAmountDayThirtyOneToSixty?.cents! /
                      totals.supplierOpenEligibleAmount?.cents!
                    : 0
            });

            invoiceSummaryRanges.push({
                index: 2,
                range: 'SIXTYONEPLUS',
                totalAmount: totals.supplierOpenEligibleAmountDaySixtyOnePlus,
                amountPercentOfTotal: isFinite(
                    totals.supplierOpenEligibleAmountDaySixtyOnePlus?.cents! /
                        totals.supplierOpenEligibleAmount?.cents!
                )
                    ? totals.supplierOpenEligibleAmountDaySixtyOnePlus?.cents! /
                      totals.supplierOpenEligibleAmount?.cents!
                    : 0
            });

            const mappedResp = new SupplierDashboardInvoiceSummaryResponse({
                ok: true,
                totalInvoiceAmount: totals.supplierOpenEligibleAmount,
                totalNumberOfInvoices: totals.supplierOpenEligiblePayableCount,
                invoiceSummaryRanges
            });
            return mappedResp;
        }
        return res as ListResponseFundingBatchSummaryDto;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
